import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { ArrowLeft, LogOut, Bookmark, Save, User, Building2, MapPin, TrendingUp, Banknote, FileText, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ASSET_CLASSES = ['Private Equity', 'Venture Capital', 'Private Debt', 'Venture Debt'];
const SECTORS = ['Healthcare & Life Sciences', 'Real Estate', 'Technology & SaaS', 'Fintech', 'Logistics & Supply Chain', 'Manufacturing', 'Consumer', 'Infrastructure', 'Renewable Energy', 'Education'];

export default function ProfilePage() {
  const { user, logout, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [ndas, setNdas] = useState([]);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await fetch(`${API}/profile`, { credentials: 'include' });
        if (res.ok) setProfile(await res.json());
      } catch {}
      setLoading(false);
    };
    const fetchNdas = async () => {
      try {
        const res = await fetch(`${API}/ndas/user`, { credentials: 'include' });
        if (res.ok) setNdas(await res.json());
      } catch {}
    };
    fetchProfile();
    fetchNdas();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaved(false);
    try {
      const res = await fetch(`${API}/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          entity_name: profile.entity_name || '',
          aum_range: profile.aum_range || '',
          asset_class_interest: profile.asset_class_interest || [],
          phone: profile.phone || '',
          city: profile.city || '',
          investment_min_ticket: profile.investment_min_ticket || '',
          investment_max_ticket: profile.investment_max_ticket || '',
          preferred_tenor: profile.preferred_tenor || '',
          preferred_sectors: profile.preferred_sectors || [],
          bio: profile.bio || '',
        }),
      });
      if (res.ok) {
        setProfile(await res.json());
        setSaved(true);
        await checkAuth();
        setTimeout(() => setSaved(false), 3000);
      }
    } catch {}
    setSaving(false);
  };

  const toggleArrayItem = (field, item) => {
    setProfile(prev => {
      const arr = prev[field] || [];
      return { ...prev, [field]: arr.includes(item) ? arr.filter(x => x !== item) : [...arr, item] };
    });
  };

  const handleLogout = async () => { await logout(); navigate('/'); };

  const handleDownloadNDA = (ndaId) => {
    window.open(`${API}/ndas/${ndaId}/pdf`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1A1A1A] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#A0743A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1A1A1A]" data-testid="profile-page">
      <header className="border-b border-[#A0743A]/20 bg-[#1A1A1A]/95 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <Link to="/" className="text-[#A0743A] text-lg tracking-[0.15em]" style={{ fontFamily: "'Cormorant Garamond', Cambria, Georgia, serif" }}>SÀWAI CAPITAL</Link>
          <div className="flex items-center gap-6">
            <Link to="/portal" className="text-[#FAF8F5] hover:text-[#A0743A] transition-colors text-xs uppercase tracking-wider" data-testid="nav-deals">Deals</Link>
            <Link to="/portal/watchlist" className="text-[#FAF8F5] hover:text-[#A0743A] transition-colors text-xs uppercase tracking-wider flex items-center gap-2" data-testid="nav-watchlist"><Bookmark size={14} /> Watchlist</Link>
            {['super_admin','admin','deal_manager','content_manager','investor_relations','view_only'].includes(user?.role) && <Link to="/admin" className="text-[#C9A96E] hover:text-[#A0743A] transition-colors text-xs uppercase tracking-wider">Admin</Link>}
            <div className="flex items-center gap-3 pl-6 border-l border-[#A0743A]/20">
              <span className="text-[#999] text-xs hidden sm:block">{user?.name}</span>
              <button onClick={handleLogout} className="text-[#999] hover:text-[#FAF8F5] transition-colors" data-testid="logout-btn"><LogOut size={16} /></button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 md:px-12 py-10">
        <Link to="/portal" className="flex items-center gap-2 text-[#A0743A] text-sm hover:text-[#C9A96E] transition-colors mb-8" data-testid="back-to-deals">
          <ArrowLeft size={16} /> Back to Deals
        </Link>

        <div className="mb-10">
          <p className="text-[#C9A96E] text-sm uppercase tracking-[0.2em] font-semibold mb-2">My Profile</p>
          <h1 className="text-3xl text-[#FAF8F5] tracking-tight">Investment Mandate & Preferences</h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Profile Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSave} className="space-y-8">
              {/* Identity */}
              <div className="border border-[#A0743A]/20 p-8">
                <div className="flex items-center gap-3 mb-6">
                  <User size={18} className="text-[#A0743A]" strokeWidth={1.25} />
                  <h3 className="text-lg text-[#FAF8F5]" style={{ fontFamily: "'Cormorant Garamond', Cambria, Georgia, serif" }}>Personal Details</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-sm tracking-wide text-[#A0743A] mb-2 block">Name</label>
                    <p className="text-[#FAF8F5] py-2 border-b border-[#A0743A]/10">{profile?.name}</p>
                  </div>
                  <div>
                    <label className="text-sm tracking-wide text-[#A0743A] mb-2 block">Email</label>
                    <p className="text-[#FAF8F5] py-2 border-b border-[#A0743A]/10">{profile?.email}</p>
                  </div>
                  <div>
                    <label className="text-sm tracking-wide text-[#A0743A] mb-2 block">Phone</label>
                    <input data-testid="profile-phone" value={profile?.phone || ''} onChange={e => setProfile(p => ({ ...p, phone: e.target.value }))} className="bg-transparent border-b border-[#A0743A]/40 text-[#FAF8F5] placeholder-[#555] py-2 focus:border-[#C9A96E] focus:outline-none w-full" placeholder="+91 98765 43210" />
                  </div>
                  <div>
                    <label className="text-sm tracking-wide text-[#A0743A] mb-2 block">City</label>
                    <input data-testid="profile-city" value={profile?.city || ''} onChange={e => setProfile(p => ({ ...p, city: e.target.value }))} className="bg-transparent border-b border-[#A0743A]/40 text-[#FAF8F5] placeholder-[#555] py-2 focus:border-[#C9A96E] focus:outline-none w-full" placeholder="Mumbai" />
                  </div>
                </div>
              </div>

              {/* Entity */}
              <div className="border border-[#A0743A]/20 p-8">
                <div className="flex items-center gap-3 mb-6">
                  <Building2 size={18} className="text-[#A0743A]" strokeWidth={1.25} />
                  <h3 className="text-lg text-[#FAF8F5]" style={{ fontFamily: "'Cormorant Garamond', Cambria, Georgia, serif" }}>Entity Information</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-sm tracking-wide text-[#A0743A] mb-2 block">Entity Name</label>
                    <input data-testid="profile-entity" value={profile?.entity_name || ''} onChange={e => setProfile(p => ({ ...p, entity_name: e.target.value }))} className="bg-transparent border-b border-[#A0743A]/40 text-[#FAF8F5] placeholder-[#555] py-2 focus:border-[#C9A96E] focus:outline-none w-full" placeholder="Family Office / Company" />
                  </div>
                  <div>
                    <label className="text-sm tracking-wide text-[#A0743A] mb-2 block">AUM / Net Worth Range</label>
                    <select data-testid="profile-aum" value={profile?.aum_range || ''} onChange={e => setProfile(p => ({ ...p, aum_range: e.target.value }))} className="bg-[#1A1A1A] border-b border-[#A0743A]/40 text-[#FAF8F5] py-2 focus:border-[#C9A96E] focus:outline-none w-full">
                      <option value="">Select range</option>
                      <option value="5-25 Cr">INR 5-25 Crore</option>
                      <option value="25-100 Cr">INR 25-100 Crore</option>
                      <option value="100-500 Cr">INR 100-500 Crore</option>
                      <option value="500+ Cr">INR 500+ Crore</option>
                    </select>
                  </div>
                </div>
                <div className="mt-6">
                  <label className="text-sm tracking-wide text-[#A0743A] mb-2 block">Bio / Investment Philosophy</label>
                  <textarea data-testid="profile-bio" value={profile?.bio || ''} onChange={e => setProfile(p => ({ ...p, bio: e.target.value }))} rows={3} className="bg-transparent border border-[#A0743A]/40 text-[#FAF8F5] placeholder-[#555] p-3 focus:border-[#C9A96E] focus:outline-none w-full resize-none" placeholder="Brief description of your investment approach..." />
                </div>
              </div>

              {/* Investment Mandate */}
              <div className="border border-[#A0743A]/20 p-8">
                <div className="flex items-center gap-3 mb-6">
                  <TrendingUp size={18} className="text-[#A0743A]" strokeWidth={1.25} />
                  <h3 className="text-lg text-[#FAF8F5]" style={{ fontFamily: "'Cormorant Garamond', Cambria, Georgia, serif" }}>Investment Mandate</h3>
                </div>

                <div className="mb-6">
                  <label className="text-sm tracking-wide text-[#A0743A] mb-3 block">Asset Class Interest</label>
                  <div className="flex flex-wrap gap-2">
                    {ASSET_CLASSES.map(cls => (
                      <button key={cls} type="button" data-testid={`profile-asset-${cls.toLowerCase().replace(/\s+/g, '-')}`} onClick={() => toggleArrayItem('asset_class_interest', cls)}
                        className={`px-4 py-2 text-xs uppercase tracking-wider transition-colors ${(profile?.asset_class_interest || []).includes(cls) ? 'bg-[#A0743A] text-[#FAF8F5]' : 'border border-[#A0743A]/40 text-[#A0743A] hover:bg-[#A0743A]/10'}`}
                      >{cls}</button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                  <div>
                    <label className="text-sm tracking-wide text-[#A0743A] mb-2 block">Min Ticket Size</label>
                    <input data-testid="profile-min-ticket" value={profile?.investment_min_ticket || ''} onChange={e => setProfile(p => ({ ...p, investment_min_ticket: e.target.value }))} className="bg-transparent border-b border-[#A0743A]/40 text-[#FAF8F5] placeholder-[#555] py-2 focus:border-[#C9A96E] focus:outline-none w-full" placeholder="INR 1 Cr" />
                  </div>
                  <div>
                    <label className="text-sm tracking-wide text-[#A0743A] mb-2 block">Max Ticket Size</label>
                    <input data-testid="profile-max-ticket" value={profile?.investment_max_ticket || ''} onChange={e => setProfile(p => ({ ...p, investment_max_ticket: e.target.value }))} className="bg-transparent border-b border-[#A0743A]/40 text-[#FAF8F5] placeholder-[#555] py-2 focus:border-[#C9A96E] focus:outline-none w-full" placeholder="INR 25 Cr" />
                  </div>
                  <div>
                    <label className="text-sm tracking-wide text-[#A0743A] mb-2 block">Preferred Tenor</label>
                    <input data-testid="profile-tenor" value={profile?.preferred_tenor || ''} onChange={e => setProfile(p => ({ ...p, preferred_tenor: e.target.value }))} className="bg-transparent border-b border-[#A0743A]/40 text-[#FAF8F5] placeholder-[#555] py-2 focus:border-[#C9A96E] focus:outline-none w-full" placeholder="2-5 Years" />
                  </div>
                </div>

                <div>
                  <label className="text-sm tracking-wide text-[#A0743A] mb-3 block">Preferred Sectors</label>
                  <div className="flex flex-wrap gap-2">
                    {SECTORS.map(s => (
                      <button key={s} type="button" data-testid={`profile-sector-${s.toLowerCase().replace(/[&\s]+/g, '-')}`} onClick={() => toggleArrayItem('preferred_sectors', s)}
                        className={`px-3 py-1.5 text-[10px] uppercase tracking-wider transition-colors ${(profile?.preferred_sectors || []).includes(s) ? 'bg-[#A0743A] text-[#FAF8F5]' : 'border border-[#A0743A]/30 text-[#A0743A] hover:bg-[#A0743A]/10'}`}
                      >{s}</button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Save */}
              <div className="flex items-center gap-4">
                <button type="submit" disabled={saving} data-testid="profile-save-btn" className="bg-[#A0743A] text-[#FAF8F5] px-8 py-3 text-sm uppercase tracking-wider font-semibold hover:bg-[#C9A96E] transition-colors disabled:opacity-50 flex items-center gap-2">
                  {saving ? 'Saving...' : <><Save size={14} /> Save Profile</>}
                </button>
                {saved && <span className="flex items-center gap-2 text-emerald-400 text-sm"><CheckCircle2 size={16} /> Profile saved</span>}
              </div>
            </form>
          </div>

          {/* Sidebar - Signed NDAs */}
          <div>
            <div className="border border-[#A0743A]/20 p-6 sticky top-24">
              <div className="flex items-center gap-2 mb-6">
                <FileText size={16} className="text-[#A0743A]" strokeWidth={1.25} />
                <h3 className="text-sm text-[#C9A96E] uppercase tracking-[0.15em] font-semibold">My Signed NDAs</h3>
              </div>
              {ndas.length === 0 ? (
                <p className="text-[#999] text-sm">No NDAs signed yet.</p>
              ) : (
                <div className="space-y-3" data-testid="profile-ndas-list">
                  {ndas.map(nda => (
                    <div key={nda.nda_id} className="border border-[#A0743A]/15 p-3">
                      <p className="text-[#FAF8F5] text-sm mb-1">Deal: {nda.deal_id}</p>
                      <p className="text-[#999] text-xs mb-2">
                        {nda.signed_at ? new Date(nda.signed_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                      </p>
                      <button
                        data-testid={`download-nda-${nda.nda_id}`}
                        onClick={() => handleDownloadNDA(nda.nda_id)}
                        className="flex items-center gap-1.5 text-[#A0743A] text-xs uppercase tracking-wider hover:text-[#C9A96E] transition-colors"
                      >
                        <FileText size={12} /> Download PDF
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
